import { api } from "@/lib/api";

const base = "/api/expenses";

export type ExpenseWorkflowStatus =
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "PAID";

export type ExpensePaymentStatus = "UNPAID" | "PARTIALLY_PAID" | "PAID";

export interface ExpenseListSummary {
  count: number;
  totalAmount: number;
  paidAmount: number;
}

export interface ExpensePaginatedList<T = unknown> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  summary?: ExpenseListSummary;
}

export interface ExpenseMeta {
  patientCategories: string[];
  centralCategories: string[];
  centralCategoriesAll: string[];
  workflowStatuses: ExpenseWorkflowStatus[];
  paymentStatuses: ExpensePaymentStatus[];
  paymentModes: string[];
}

export type ExpenseEmployeeScope = "CENTRAL" | "UNIT";
export interface ExpenseEmployeeRow {
  id: string;
  name: string;
  scope: ExpenseEmployeeScope;
  monthlyAmount?: number | null;
  isActive?: boolean;
  lastEmploymentDate?: string | null;
  bankDetails?: string | null;
  notes?: string | null;
  hospitalId?: string;
  hospital?: { id: string; name: string } | null;
  updatedAt?: string;
}

export type SalaryRunPreviewLine = {
  employeeId: string;
  name: string;
  amount: number;
  scope: ExpenseEmployeeScope;
  hospitalId: string;
  hospitalName: string;
  hospitalUnit: string;
  alreadyBooked: boolean;
  existingExpenseId: string | null;
  prorationNote?: string | null;
};

export type SalaryRunPreview = {
  serviceMonth: string;
  lines: SalaryRunPreviewLine[];
  toBookCount: number;
  toBookTotal: number;
  skippedCount: number;
};

/** IPD admission row for expense patient picker */
export interface AdmissionPickerItem {
  id: string;
  admissionDate: string;
  /** Null while still admitted. */
  dischargeDate?: string | null;
  status: string;
  patientId: string;
  ipdNumber: string;
  patientName: string;
  patientUhid: string | null;
  assignedDoctorId?: string | null;
  assignedDoctorName?: string | null;
  assignedDoctorSpecialisation?: string | null;
  surgeryName?: string | null;
  surgerySpecialisation?: string | null;
  packageSurgeries?: Array<{
    id?: string | null;
    specialisation?: string | null;
    surgeryName: string;
    sortOrder?: number;
  }>;
}

export type AdmissionCostLineSource = "PATIENT_EXPENSE" | "PHARMA" | "LAB";

export interface AdmissionPharmaCostItem {
  id: string;
  kind: "IPD_ISSUE" | "OT_PHARMA";
  label: string;
  quantity: number;
  unitCost: number;
  amount: number;
  date: string;
  /** When false, omitted from P&L IPD pharma and admission cost report totals. */
  includedInReports?: boolean;
}

export interface AdmissionLabCostItem {
  id: string;
  label: string;
  processingType: string;
  customerPrice: number;
  externalCost: number;
  margin: number;
  total: number;
  vendor: string | null;
  date: string;
}

export interface AdmissionCostLine {
  id: string;
  source: AdmissionCostLineSource;
  date: string;
  label: string;
  category: string;
  amount: number;
  editable: boolean;
  /** Patient expenses only; null for consolidated pharma/lab module costs. */
  paymentStatus: ExpensePaymentStatus | null;
  paidAmount: number | null;
  /** Lab external module cost — vendor display name. */
  vendorName?: string | null;
  /** Lab external module cost — vendor purchase cost. */
  vendorCost?: number | null;
  /** INTERNAL | EXTERNAL for lab module cost lines. */
  labKind?: "INTERNAL" | "EXTERNAL" | null;
}

export const expensesApi = {
  getMeta: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: ExpenseMeta }>(`${base}/meta`, { params }),

  searchAdmissions: (params?: { q?: string; limit?: number; hospitalId?: string }) =>
    api.get<{ message: string; data: AdmissionPickerItem[] }>(`${base}/admissions`, { params }),

  getAdmissionBillingSummary: (admissionId: string, params?: { hospitalId?: string }) =>
    api.get<{
      message: string;
      data: {
        admissionId: string;
        ipdNumber: string;
        admissionStatus: string;
        billNumber: string | null;
        billStatus: string | null;
        netAmount: number | null;
        totalAccrued?: number | null;
        totalDeposits?: number | null;
        outstanding?: number | null;
        pendingAmount?: number | null;
        excessAmount?: number | null;
        pendingThirdPartyCredit?: number | null;
        isFullyCollected?: boolean;
        partnerName?: string | null;
        referrerName?: string | null;
        salesPersonName?: string | null;
        financingPartnerName?: string | null;
      };
    }>(`${base}/admissions/${admissionId}/billing-summary`, { params }),

  setPharmaCostReportInclusion: (
    kind: AdmissionPharmaCostItem['kind'],
    id: string,
    included: boolean
  ) =>
    api
      .patch<{ message: string; data: { id: string; kind: string; includedInReports: boolean } }>(
        `${base}/pharma-cost/${kind}/${id}/report-inclusion`,
        { included }
      )
      .then((r) => r.data.data),

  getAdmissionCostLines: (admissionId: string, params?: { hospitalId?: string }) =>
    api.get<{
      message: string;
      data: {
        lines: AdmissionCostLine[];
        pharmaItems: AdmissionPharmaCostItem[];
        labItems: AdmissionLabCostItem[];
        totals: {
          patientExpense: number;
          pharma: number;
          lab: number;
          labInternal: number;
          labExternal: number;
          /** Manual + pharma + internal lab; external lab vendor cost excluded. */
          all: number;
        };
      };
    }>(`${base}/admissions/${admissionId}/cost-lines`, { params }),

  listPatient: (params?: Record<string, string | undefined>) =>
    api.get<{ message: string; data: ExpensePaginatedList }>(`${base}/patient`, { params }),

  listPatientPayeeVendors: (params: { category: string; hospitalId?: string }) =>
    api.get<{ message: string; data: { names: string[] } }>(`${base}/patient/payee-vendors`, { params }),

  listB2bPartners: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: { names: string[] } }>(`${base}/patient/b2b-partners`, { params }),

  createB2bPartner: (body: { name: string; hospitalId?: string }) =>
    api.post<{ message: string; data: { name: string } }>(`${base}/patient/b2b-partners`, body),

  createPatient: (body: Record<string, unknown>) =>
    api.post(`${base}/patient`, body),

  updatePatient: (id: string, body: Record<string, unknown>) =>
    api.patch(`${base}/patient/${id}`, body),

  submitPatient: (id: string) => api.post(`${base}/patient/${id}/submit`),

  approvePatient: (id: string) => api.post(`${base}/patient/${id}/approve`),

  rejectPatient: (id: string, reason?: string) =>
    api.post(`${base}/patient/${id}/reject`, { reason }),

  markPatientPaid: (
    id: string,
    body: { paymentMode: string; paidAmount?: number; paymentStatus?: ExpensePaymentStatus }
  ) => api.post(`${base}/patient/${id}/mark-paid`, body),

  /** Hospital admin: reverse wrongly marked paid → APPROVED / unpaid. */
  unmarkPatientPaid: (id: string) => api.post(`${base}/patient/${id}/unmark-paid`),

  uploadPatientProof: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`${base}/patient/${id}/proof`, fd);
  },

  deletePatientProof: (id: string) => api.delete(`${base}/patient/${id}/proof`),

  deletePatientProofById: (id: string, proofId: string) =>
    api.delete(`${base}/patient/${id}/proofs/${proofId}`),

  deletePatient: (id: string) => api.delete(`${base}/patient/${id}`),

  listCentral: (params?: Record<string, string | undefined>) =>
    api.get<{ message: string; data: ExpensePaginatedList }>(`${base}/central`, { params }),

  listCentralPayeeVendors: (params: {
    category?: string;
    q?: string;
    all?: boolean;
    limit?: number;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: { names: string[] } }>(`${base}/central/payee-vendors`, {
      params: {
        ...params,
        all: params.all ? "true" : undefined,
      },
    }),

  listCentralEmployees: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: { employees: ExpenseEmployeeRow[] } }>(`${base}/central/employees`, { params }),

  createCentralEmployee: (body: {
    name: string;
    scope: ExpenseEmployeeScope;
    hospitalId?: string;
    hospitalUnit?: string;
  }) => api.post(`${base}/central/employees`, body),

  listSalaryRoster: (params?: { includeInactive?: boolean; hospitalId?: string }) =>
    api.get<{ message: string; data: { employees: ExpenseEmployeeRow[] } }>(
      `${base}/salary-roster`,
      {
        params: {
          hospitalId: params?.hospitalId,
          includeInactive: params?.includeInactive ? "true" : undefined,
        },
      }
    ),

  upsertSalaryRoster: (body: {
    id?: string;
    name: string;
    monthlyAmount: number;
    scope?: ExpenseEmployeeScope;
    hospitalUnit?: string;
    targetHospitalId?: string;
    bankDetails?: string;
    notes?: string;
    isActive?: boolean;
    lastEmploymentDate?: string;
  }) => api.post(`${base}/salary-roster`, body),

  moveSalaryRoster: (
    id: string,
    body: { targetHospitalId?: string; hospitalUnit?: string }
  ) => api.post(`${base}/salary-roster/${id}/move`, body),

  previewSalaryRun: (params: { serviceMonth: string; hospitalId?: string }) =>
    api.get<{ message: string; data: SalaryRunPreview }>(`${base}/salary-run/preview`, {
      params,
    }),

  runSalaryRun: (body: {
    serviceMonth: string;
    /** Default mode when an employee has no override. */
    paymentMode?: string;
    /** Per-employee payment mode overrides (employeeId → mode). */
    employeePaymentModes?: Record<string, string>;
    /** Per-employee amount for this run (employeeId → amount). Defaults to roster monthlyAmount. */
    employeeAmounts?: Record<string, number>;
    expenseDate?: string;
    hospitalId?: string;
  }) => api.post(`${base}/salary-run`, body),

  listOfflineMarketingSubcategories: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: { names: string[] } }>(
      `${base}/central/offline-marketing-subcategories`,
      { params }
    ),

  createOfflineMarketingSubcategory: (body: { name: string; hospitalId?: string }) =>
    api.post(`${base}/central/offline-marketing-subcategories`, body),

  createCentral: (body: Record<string, unknown>) =>
    api.post(`${base}/central`, body),

  updateCentral: (id: string, body: Record<string, unknown>) =>
    api.patch(`${base}/central/${id}`, body),

  submitCentral: (id: string) => api.post(`${base}/central/${id}/submit`),

  approveCentral: (id: string) => api.post(`${base}/central/${id}/approve`),

  rejectCentral: (id: string, reason?: string) =>
    api.post(`${base}/central/${id}/reject`, { reason }),

  markCentralPaid: (
    id: string,
    body: { paymentMode: string; paidAmount?: number; paymentStatus?: ExpensePaymentStatus }
  ) => api.post(`${base}/central/${id}/mark-paid`, body),

  /** Hospital admin: reverse wrongly marked paid → APPROVED / unpaid. */
  unmarkCentralPaid: (id: string) => api.post(`${base}/central/${id}/unmark-paid`),

  uploadCentralProof: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`${base}/central/${id}/proof`, fd);
  },

  deleteCentralProof: (id: string) => api.delete(`${base}/central/${id}/proof`),

  deleteCentralProofById: (id: string, proofId: string) =>
    api.delete(`${base}/central/${id}/proofs/${proofId}`),

  deleteCentral: (id: string) => api.delete(`${base}/central/${id}`),

  getPending: (params?: { hospitalId?: string }) =>
    api.get(`${base}/pending`, { params }),

  getUnpaid: (params?: { hospitalId?: string }) =>
    api.get(`${base}/unpaid`, { params }),

  reportPatientProfitability: (params?: Record<string, string | undefined>) =>
    api.get(`${base}/reports/patient-profitability`, { params }),

  reportDoctorPayouts: (params?: Record<string, string | undefined>) =>
    api.get(`${base}/reports/doctor-payouts`, { params }),

  reportReferrals: (params?: Record<string, string | undefined>) =>
    api.get(`${base}/reports/referrals`, { params }),

  reportCentralMonthly: (params?: Record<string, string | number | undefined>) =>
    api.get(`${base}/reports/central-monthly`, { params }),

  /** Smart pending payments: unpaid IPD/Central + missing OPD/surgeon payout alerts. */
  reportPendingPayments: (params?: {
    hospitalId?: string;
    from?: string;
    to?: string;
  }) =>
    api.get<{ message: string; data: PendingPaymentsReport }>(
      `${base}/reports/pending-payments`,
      { params }
    ),

  // TDS status
  listTdsStatus: (params?: { hospitalId?: string; from?: string; to?: string; tdsPaid?: string }) =>
    api.get(`${base}/tds-status`, { params }),
  exportTdsStatus: (params?: { hospitalId?: string; from?: string; to?: string; tdsPaid?: string }) =>
    api.get<Blob>(`${base}/tds-status/export`, { params, responseType: 'blob' }),
  markPatientTdsPaid: (id: string, body?: { tdsChallanNo?: string | null }) =>
    api.post(`${base}/patient/${id}/tds-paid`, body || {}),
  markCentralTdsPaid: (id: string, body?: { tdsChallanNo?: string | null }) =>
    api.post(`${base}/central/${id}/tds-paid`, body || {}),

  listImprest: (params?: {
    hospitalId?: string;
    targetHospitalId?: string;
    hospitalUnit?: string;
    includeClosed?: boolean;
  }) =>
    api.get<{ message: string; data: ImprestListResponse }>(`${base}/imprest`, {
      params: {
        hospitalId: params?.hospitalId,
        targetHospitalId: params?.targetHospitalId,
        hospitalUnit: params?.hospitalUnit,
        includeClosed: params?.includeClosed ? "true" : undefined,
      },
    }),
  listImprestPayees: (params?: {
    hospitalId?: string;
    targetHospitalId?: string;
    hospitalUnit?: string;
  }) =>
    api.get<{
      message: string;
      data: {
        employees: Array<{
          id: string;
          name: string;
          role: string;
          email: string;
          status: string;
          dept?: { id: string; name: string } | null;
        }>;
        hospitalId: string;
      };
    }>(`${base}/imprest/payees`, {
      params: {
        hospitalId: params?.hospitalId,
        targetHospitalId: params?.targetHospitalId,
        hospitalUnit: params?.hospitalUnit,
      },
    }),
  listImprestTxns: (id: string, params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: { transactions: ImprestTxnRow[] } }>(
      `${base}/imprest/${id}/txns`,
      { params }
    ),
  updateImprestSettlement: (
    txnId: string,
    body: { amount?: number; notes?: string | null },
    params?: { hospitalId?: string }
  ) =>
    api.patch<{ message: string; data: { txn: ImprestTxnRow; remainingBalance: number | null } }>(
      `${base}/imprest/txns/${txnId}`,
      body,
      { params }
    ),
  deleteImprestSettlement: (txnId: string, params?: { hospitalId?: string }) =>
    api.delete<{ message: string; data: { restoredAmount: number; remainingBalance: number } }>(
      `${base}/imprest/txns/${txnId}`,
      { params }
    ),
  issueImprest: (body: {
    hospitalStaffId: string;
    amount: number;
    paymentMode: string;
    notes?: string;
    txnAt?: string;
    hospitalId?: string;
    targetHospitalId?: string;
    hospitalUnit?: string;
  }) => api.post<{ message: string; data: ImprestAdvanceRow }>(`${base}/imprest/issue`, body),
};

export type PendingPaymentUrgency = 'CRITICAL' | 'HIGH' | 'WATCH';

export interface PendingExpenseRow {
  kind: 'patient' | 'central';
  id: string;
  category: string;
  subCategory: string | null;
  description: string | null;
  amount: number;
  paidAmount: number;
  balance: number;
  expenseDate: string;
  serviceMonth: string | null;
  workflowStatus: string;
  paymentStatus: string;
  payeeName: string | null;
  patientName: string | null;
  patientUhid: string | null;
  ipdNumber: string | null;
  hospitalUnit: string | null;
  createdByName: string | null;
  ageDays: number;
  urgency: PendingPaymentUrgency;
}

export interface MissingOpdPayoutAlert {
  key: string;
  doctorId: string;
  doctorName: string;
  serviceMonth: string;
  consultationCount: number;
  consultationRevenue: number;
  firstBillDate: string;
  lastBillDate: string;
  ageDays: number;
  urgency: PendingPaymentUrgency;
  samplePatients: Array<{ name: string; uhid: string | null; billDate: string }>;
}

export interface MissingIpdSurgeonAlert {
  admissionId: string;
  ipdNumber: string | null;
  patientName: string | null;
  patientUhid: string | null;
  dischargeDate: string;
  surgeryName: string | null;
  assignedDoctorId: string | null;
  assignedDoctorName: string | null;
  ageDays: number;
  urgency: PendingPaymentUrgency;
}

export interface PendingPaymentsReport {
  asOf: string;
  from: string | null;
  to: string | null;
  summary: {
    ipdCount: number;
    ipdAmount: number;
    centralCount: number;
    centralAmount: number;
    missingOpdCount: number;
    missingIpdSurgeonCount: number;
    oldestAgeDays: number;
  };
  ipd: PendingExpenseRow[];
  central: PendingExpenseRow[];
  alerts: {
    missingOpdPayouts: MissingOpdPayoutAlert[];
    missingIpdSurgeonPayouts: MissingIpdSurgeonAlert[];
  };
}

export type ExpenseFundingSource = "REGULAR" | "IMPREST";

export interface ImprestAdvanceRow {
  id: string;
  hospitalId: string;
  hospitalStaffId: string;
  hospitalUnit?: string;
  issuedAmount: number;
  remainingBalance: number;
  status: "OPEN" | "CLOSED";
  notes: string | null;
  issuedByName: string | null;
  hospitalStaff?: { id: string; name: string; role: string; email: string; status?: string };
  hospital?: { id: string; name: string };
  updatedAt?: string;
}

export interface ImprestListResponse {
  advances: ImprestAdvanceRow[];
  myBalance: number;
  myAdvanceId: string | null;
  defaultFundingSource: ExpenseFundingSource;
}

export interface ImprestTxnLinkedCentralExpense {
  id: string;
  expenseNumber: string | null;
  category: string;
  subCategory: string | null;
  vendorName: string | null;
  description: string | null;
  amount: number;
  expenseDate: string;
  hospitalUnit: string;
  workflowStatus: string;
  paymentMode: string | null;
  paidAt: string | null;
  createdByName: string | null;
  fundedByImprest: boolean;
  createdBy?: { name: string | null } | null;
  _count?: { proofs: number };
}

export interface ImprestTxnLinkedPatientExpense {
  id: string;
  expenseNumber: string | null;
  category: string;
  subCategory: string | null;
  description: string | null;
  amount: number;
  expenseDate: string;
  payeeVendorName: string | null;
  workflowStatus: string;
  paymentMode: string | null;
  paidAt: string | null;
  createdByName: string | null;
  patient?: { name: string | null } | null;
  _count?: { proofs: number };
}

export interface ImprestTxnRow {
  id: string;
  type: "ISSUE" | "SETTLEMENT" | "ADJUSTMENT";
  amount: number;
  paymentMode: string | null;
  notes: string | null;
  txnAt: string;
  createdByName: string | null;
  patientExpenseId: string | null;
  centralExpenseId: string | null;
  centralExpense?: ImprestTxnLinkedCentralExpense | null;
  patientExpense?: ImprestTxnLinkedPatientExpense | null;
}

